import React, {useEffect, useRef, useState} from "react";
import L from "leaflet";
import {
    configureLeafletMarkerIcons,
    getTileLayerPreference,
    addTileLayerWithFallback,
    TURKEY_BOUNDS
} from "../../../utils/helpers";
import {TileLayerControl} from "../TileLayerControl";

export const LeafletMapBase = ({
    center = {lat: 41.0082, lng: 28.9784},
    zoom = 11,
    onMapReady,
    onMapClick,
    children,
    style = {width: "100%", height: "100%"},
    className = "",
    tileLayerRef: externalTileLayerRef = null,
    showTileLayerControl = true,
    tileLayerControlStyle = {}
}) => {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const internalTileLayerRef = useRef(null);
    const tileLayerRef = externalTileLayerRef || internalTileLayerRef;
    const [isMapReady, setIsMapReady] = useState(false);

    // Initialize Leaflet map
    useEffect(() => {
        if (mapRef.current && !mapInstanceRef.current) {
            // Configure Leaflet marker icons using helper function
            configureLeafletMarkerIcons(L);

            // Türkiye sınırları
            const turkeyBounds = L.latLngBounds(TURKEY_BOUNDS.southWest, TURKEY_BOUNDS.northEast);

            mapInstanceRef.current = L.map(mapRef.current, {
                maxBounds: turkeyBounds,
                maxBoundsViscosity: 1.0,
                minZoom: 7,
                maxZoom: 22
            }).setView([center.lat, center.lng], zoom);

            // Add tile layer with fallback
            tileLayerRef.current = addTileLayerWithFallback(
                L,
                mapInstanceRef.current,
                getTileLayerPreference()
            );

            // Ensure map controls have proper z-index
            const mapContainer = mapInstanceRef.current.getContainer();
            if (mapContainer) {
                const controls = mapContainer.querySelectorAll(".leaflet-control");
                controls.forEach(control => {
                    control.style.zIndex = "1000";
                });
            }

            // Add click event if provided
            if (onMapClick) {
                mapInstanceRef.current.on("click", e => {
                    // Convert Leaflet event to the format expected by handleMapClick
                    const lat = e.latlng.lat;
                    const lng = e.latlng.lng;
                    onMapClick({lat, lng});
                });
            }

            // Notify parent that map is ready
            if (onMapReady) {
                onMapReady(mapInstanceRef.current);
            }

            setIsMapReady(true);
        }

        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
                setIsMapReady(false);
            }
        };
    }, []);

    // Only update map center and zoom on initial load, not on every change
    useEffect(() => {
        if (mapInstanceRef.current && isMapReady) {
            // Only set initial view, don't update on every center/zoom change
            // This prevents interfering with user zoom/pan actions
        }
    }, [isMapReady]);

    return (
        <div className={className} style={{...style, position: "relative"}}>
            <div ref={mapRef} style={{width: "100%", height: "100%"}} />
            {isMapReady && children}
            {/* Tile Layer Control */}
            {isMapReady && showTileLayerControl && (
                <TileLayerControl
                    mapInstance={mapInstanceRef.current}
                    tileLayerRef={tileLayerRef}
                    style={tileLayerControlStyle}
                />
            )}
        </div>
    );
};
